/**
 * Single source of truth for all portfolio content.
 *
 * Every section of the site reads from this file, so updating the portfolio
 * means editing data here rather than touching JSX.
 *
 * NOTE: entries set to `null` are links that are not known yet. Components
 * skip null links instead of rendering a dead anchor, so it is safe to leave
 * them until the real URL is available.
 */

export const profile = {
  name: "Nikhil Raj",
  role: "Software Developer",
  focus: "AI / Machine Learning & Full-Stack Development",
  location: "Haldia, West Bengal, India",
  tagline:
    "Computer Science undergraduate building ML-powered and real-time monitoring applications.",
  summary:
    "Computer Science and Engineering undergraduate at Haldia Institute of Technology with a CGPA of 8.7, focused on software development, Artificial Intelligence, and Machine Learning. Proficient in C++, Python, JavaScript, MERN Stack, Data Structures and Algorithms, and DBMS, with hands-on experience developing and deploying ML-powered and real-time monitoring applications.",
};

export const contact = {
  email: "nikhilrazee@gmail.com",
  phone: "+91 81020 00539",
};

export const socials = [
  {
    id: "github",
    label: "GitHub",
    url: "https://github.com/nikhilraze",
    handle: "nikhilraze",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    url: "https://www.linkedin.com/in/nikhilraj6/",
    handle: "nikhilraj6",
  },
  {
    id: "twitter",
    label: "X",
    url: "https://x.com/aaryannikhil317",
    handle: "aaryannikhil317",
  },
  // Handles unknown — add the profile URLs to enable these links.
  { id: "codeforces", label: "Codeforces", url: null, handle: null },
  { id: "leetcode", label: "LeetCode", url: null, handle: null },
];

export const education = [
  {
    institution: "Haldia Institute of Technology",
    qualification: "Bachelor of Technology (B.Tech), Computer Science and Engineering",
    period: "2024 – Present",
    result: "CGPA: 8.7 / 10",
  },
];

export const experience = [
  {
    role: "Crowdfunding Intern",
    organisation: "Muskurahat Foundation",
    period: "21 Apr 2026 – 21 May 2026",
    points: [
      "Participated in crowdfunding campaigns supporting initiatives focused on education and opportunities for children in need.",
      "Conducted donor outreach and communicated campaign objectives to encourage contributions and increase awareness.",
      "Developed practical skills in fundraising, communication, outreach, persuasion, and campaign coordination.",
    ],
  },
];

export const projects = [
  {
    id: "payshield-ai",
    title: "PayShield AI",
    subtitle: "AI-Powered Security Platform",
    description:
      "An AI-focused web application addressing security and risk-related challenges through an interactive interface.",
    points: [
      "Developed an AI-focused web application addressing security and risk-related challenges through an interactive interface.",
      "Implemented application functionality with emphasis on intelligent analysis, usability, and secure handling of user inputs.",
    ],
    tech: ["Python", "Machine Learning", "Streamlit"],
    repo: null,
    demo: "https://payshield-ai.netlify.app/",
    featured: true,
  },
  {
    id: "emoji-search",
    title: "Emoji Search & Emotion Detection",
    subtitle: "Text Emotion Classifier",
    description:
      "A Machine Learning web application that predicts emotions from text and suggests relevant emojis.",
    points: [
      "Developed a Machine Learning web application that predicts emotions from text and suggests relevant emojis.",
      "Implemented text preprocessing and emotion classification using Python, Scikit-learn, Pandas, and Joblib.",
    ],
    tech: ["Python", "Scikit-learn", "Pandas", "Joblib", "Streamlit"],
    repo: null,
    demo: "https://emojisearch.streamlit.app/",
    featured: true,
  },
  {
    id: "carbon-calculator",
    title: "Carbon Calculator",
    subtitle: "Emissions Estimator",
    description:
      "An interactive calculator for estimating and tracking carbon emissions, turning everyday activity inputs into a clear footprint breakdown.",
    points: [
      "Built an interactive input flow that converts activity data into an emissions estimate.",
      "Presented results as a breakdown so users can see which activities dominate their footprint.",
    ],
    tech: ["Python", "Streamlit", "Pandas"],
    repo: null,
    demo:
      "https://carbon-calculator-sgvvpm9ywrpufnsgurv7cx.streamlit.app/",
    featured: false,
  },
];

/**
 * Headline figures shown in the hero. The project count is derived from
 * `projects` above so it cannot drift when a project is added or removed.
 */
export const highlights = [
  { value: "8.7", unit: "/10", label: "CGPA" },
  { value: String(projects.length), unit: "", label: "Projects built" },
  { value: "2024", unit: "", label: "B.Tech started" },
];

export const skillGroups = [
  {
    id: "languages",
    title: "Programming Languages",
    skills: ["C", "C++", "Python", "JavaScript"],
  },
  {
    id: "web",
    title: "Web Development",
    skills: ["MERN Stack", "React", "Node.js", "HTML5", "CSS3"],
  },
  {
    id: "cs",
    title: "Computer Science",
    skills: [
      "Data Structures & Algorithms",
      "Object-Oriented Programming",
      "Database Management Systems",
      "Operating Systems",
      "Computer Networks",
    ],
  },
  {
    id: "ml",
    title: "Machine Learning",
    skills: ["Scikit-learn", "Pandas", "Data Analysis", "ML Fundamentals"],
  },
  {
    id: "tools",
    title: "Tools & Platforms",
    skills: ["Linux", "Git", "GitHub", "VS Code", "Streamlit"],
  },
  {
    id: "problem-solving",
    title: "Problem Solving",
    skills: ["Competitive Programming", "Algorithm Design"],
  },
];

/**
 * Home-page sections exposed in the navbar. `id` must match the `id` of the
 * rendered <section> so hash navigation can scroll to it.
 */
export const navLinks = [
  { label: "About", id: "about" },
  { label: "Skills", id: "skills" },
  { label: "Experience", id: "experience" },
  { label: "Projects", id: "projects" },
  { label: "Contact", id: "contact" },
];
